import { useState, useEffect, useCallback, useContext } from 'react';
import {
    Modal, ModalDialog, ModalClose, Typography, Box,
    Button, Textarea, Radio, RadioGroup, CircularProgress,
} from '@mui/joy';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import { AppContext } from '../../context';
import { APIGetRetroNumbers, APIClaimRetro, parseApiError } from '../../api';

export default function MissedTransferModal({ open, onClose, onLinked, onSkipNumber }) {
    const { token, showToast } = useContext(AppContext);
    const [numbers,    setNumbers]    = useState([]);
    const [loading,    setLoading]    = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [reason,     setReason]     = useState('');
    const [sending,    setSending]    = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIGetRetroNumbers(token);
        setNumbers(Array.isArray(data) ? data : []);
        setLoading(false);
    }, [token]);

    useEffect(() => {
        if (open) { load(); setSelectedId(null); setReason(''); }
    }, [open, load]);

    const handleSubmit = async () => {
        if (!selectedId) { showToast('Выберите номер', 'warning'); return; }
        if (!reason.trim()) { showToast('Укажите причину', 'warning'); return; }
        setSending(true);
        const res = await APIClaimRetro(token, { payment_number_id: selectedId, reason: reason.trim() });
        setSending(false);
        if (res && res.id && !res.error) {
            onLinked(res);
        } else {
            showToast(parseApiError(res?.error || 'Не удалось привязать номер'), 'danger');
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 400, width: '100%',
                p: { xs: 2, sm: 3 },
                maxHeight: '95vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography level="h4" sx={{ fontWeight: 800, mb: 0.5 }}>
                    Номер сбросился, а перевод пришёл - это косяк
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mb: 1.5 }}>
                    Ниже — номер, время по которому истекло. Выбери тот, на который пришёл перевод, и укажи,
                    почему не успел(а) за 20 минут. Либо оформи чек без номера (только с комментарием).
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress /></Box>
                ) : numbers.length === 0 ? (
                    <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', py: 2, textAlign: 'center' }}>
                        Нет доступных номеров на твоей точке
                    </Typography>
                ) : (
                    <RadioGroup
                        value={selectedId}
                        onChange={(e) => setSelectedId(Number(e.target.value))}
                        sx={{ gap: 0.6, maxHeight: 240, overflowY: 'auto', mb: 1.5 }}
                    >
                        {numbers.map(n => (
                            <Box
                                key={n.id}
                                onClick={() => setSelectedId(n.id)}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1, p: 1,
                                    borderRadius: 'var(--joy-radius-md)',
                                    border: '1px solid',
                                    borderColor: selectedId === n.id ? 'var(--joy-palette-primary-500)' : 'var(--joy-palette-divider)',
                                    bgcolor: selectedId === n.id ? 'rgba(162,38,226,0.06)' : 'transparent',
                                    cursor: 'pointer',
                                }}
                            >
                                <Radio value={n.id} size="sm" />
                                <CreditCardRoundedIcon sx={{ fontSize: 16, color: 'var(--joy-palette-primary-400)' }} />
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                                        {n.bank_display} · {n.phone_formatted || n.phone}
                                    </Typography>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                        {n.owner_name}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </RadioGroup>
                )}

                <Typography sx={{
                    fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.06em', color: 'var(--joy-palette-text-secondary)', mb: 0.4,
                }}>
                    Комментарий (обязательно)
                </Typography>
                <Textarea
                    minRows={2} maxRows={4}
                    placeholder="Например: отвлёкся на клиента, забыл отправить чек"
                    value={reason}
                    onChange={(e) => setReason(e.target.value.slice(0, 300))}
                    sx={{ mb: 1.5 }}
                />

                <Button
                    onClick={handleSubmit}
                    loading={sending}
                    disabled={!selectedId || !reason.trim()}
                    sx={{ fontWeight: 800 }}
                >
                    Привязать номер к чеку
                </Button>

                {/* <Button
                    variant="soft" color="neutral"
                    onClick={() => {
                        if (!reason.trim()) { showToast('Комментарий обязателен', 'warning'); return; }
                        onSkipNumber?.(reason.trim());
                    }}
                    disabled={sending || !reason.trim()}
                    sx={{ mt: 1, fontWeight: 700 }}
                >
                    Оформить чек без номера
                </Button> */}
            </ModalDialog>
        </Modal>
    );
}